import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, CheckCircle, Clock, XCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { StatsCard } from '../components/StatsCard';
import { Pagination } from '../components/Pagination';
import { COLORS } from '../../theme/colors';

const LIMIT = 30;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_BADGE = {
  paid: 'admin-badge admin-badge-success',
  pending: 'admin-badge admin-badge-info',
  failed: 'admin-badge admin-badge-danger',
};

const fmtChars = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K' : n);
const fmtUah = (kopiykas) => (kopiykas / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });

export function PaymentsPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getPayments({
        status: statusFilter || undefined,
        skip: (page - 1) * LIMIT,
        limit: LIMIT,
      });
      setItems(data.items);
      setTotal(data.total);
      setSummary(data.summary);
    } catch (error) {
      console.error('Failed to load payments:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Payments</h1>
        <button className="admin-btn admin-btn-secondary" onClick={load} title="Refresh">
          <RefreshCw size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Refresh
        </button>
      </div>

      {summary && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <StatsCard
            title="Revenue (paid)"
            value={`${fmtUah(summary.revenue_kopiykas)} ₴`}
            icon={CheckCircle}
            color={COLORS.success}
          />
          <StatsCard title="Paid orders" value={summary.paid} icon={CreditCard} color={COLORS.accent} />
          <StatsCard title="Pending" value={summary.pending} icon={Clock} color={COLORS.warning} />
          <StatsCard title="Failed" value={summary.failed} icon={XCircle} color={COLORS.danger} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            className={`admin-btn ${statusFilter === f.value ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => { setStatusFilter(f.value); setPage(1); }}
            style={{ padding: '6px 16px' }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="admin-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>User</th>
              <th>Pack</th>
              <th>Chars</th>
              <th>Amount</th>
              <th>Status</th>
              <th style={{ width: 90 }}>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No payments found</td></tr>
            ) : (
              items.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(o.created_at).toLocaleString()}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {o.user_email ? (
                      <a
                        href={`mailto:${o.user_email}${o.status === 'failed' ? '?subject=' + encodeURIComponent('Your ChatsControl payment') : ''}`}
                        style={{ color: 'var(--brand-500)', textDecoration: 'none' }}
                      >
                        {o.user_email}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)' }}>deleted user</span>
                    )}
                  </td>
                  <td style={{ fontSize: 13, textTransform: 'capitalize' }}>{o.pack_key}</td>
                  <td style={{ fontSize: 13 }}>{fmtChars(o.chars)}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    ${o.usd_price}
                    <span style={{ color: 'var(--text-disabled)' }}>{' · '}{fmtUah(o.uah_amount)} ₴</span>
                  </td>
                  <td>
                    <span className={STATUS_BADGE[o.status] || 'admin-badge'}>{o.status}</span>
                  </td>
                  <td>
                    {o.receipt_url ? (
                      <a
                        href={o.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--text-muted)' }}
                        title="Open mono invoice"
                      >
                        <ExternalLink size={16} />
                      </a>
                    ) : (
                      <span style={{ color: 'var(--border-strong)' }}>-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
